// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { Checkbox, OutputFooter } from '@tls/treact-ui';
import { FlagError, FlagWarning } from '@tls/treact-ui/flags';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type Props = {
  show: boolean;
  outputFooterRef: React.RefObject<HTMLDivElement | null>;
  hasMultiLabelLicenseError: boolean;
  requiresResetConfirmation: boolean;
  modelCheckFailed: boolean;
  resetConfirmed: boolean;
  onResetConfirmationChange: () => void;
  requiresImageLabelResetConfirmation: boolean;
  imageLabelResetConfirmed: boolean;
  onImageLabelResetConfirmationChange: () => void;
};

export default function ProjectSettingsWarnings({
  show,
  outputFooterRef,
  hasMultiLabelLicenseError,
  requiresResetConfirmation,
  modelCheckFailed,
  resetConfirmed,
  onResetConfirmationChange,
  requiresImageLabelResetConfirmation,
  imageLabelResetConfirmed,
  onImageLabelResetConfirmationChange,
}: Readonly<Props>) {
  const { t } = useTranslation();

  if (!show) return null;

  if (hasMultiLabelLicenseError) {
    return (
      <OutputFooterOverlay ref={outputFooterRef}>
        <SettingsOutputFooter>
          <FooterFlag $rowSpan={1}>
            <FlagError width='1.5rem' height='1.5rem' />
          </FooterFlag>
          <FooterText $row={1} data-testid='project-settings-multilabel-license-error'>
            <span className='font-size_s font-weight_bold'>{t('project.settings.multiLabelLicense.title')}</span>
            <span className='font-size_s'>{t('project.settings.multiLabelLicense.details')}</span>
          </FooterText>
        </SettingsOutputFooter>
      </OutputFooterOverlay>
    );
  }

  const warningRowCount = Number(requiresResetConfirmation) + Number(requiresImageLabelResetConfirmation);
  const imageLabelResetRow = requiresResetConfirmation ? 2 : 1;
  const trainingDataResetDetailsKey = modelCheckFailed
    ? 'project.settings.trainingDataReset.unknownDetails'
    : 'project.settings.trainingDataReset.details';

  return (
    <OutputFooterOverlay ref={outputFooterRef}>
      <SettingsOutputFooter>
        <FooterFlag $rowSpan={warningRowCount}>
          <FlagWarning width='1.5rem' height='1.5rem' />
        </FooterFlag>
        {requiresResetConfirmation ? (
          <>
            <FooterText $row={1} data-testid='project-settings-training-data-reset-warning'>
              <span className='font-size_s font-weight_bold'>{t('project.settings.trainingDataReset.title')}</span>
              <span className='font-size_s'>{t(trainingDataResetDetailsKey)}</span>
            </FooterText>
            <FooterCheckbox $row={1}>
              <Checkbox
                asAtom
                label={t('project.settings.trainingDataReset.confirm')}
                value={resetConfirmed ? Checkbox.Selection.selected : Checkbox.Selection.unselected}
                onValueChange={onResetConfirmationChange}
                data-testid='project-settings-training-data-reset-checkbox'
              />
            </FooterCheckbox>
          </>
        ) : null}
        {requiresImageLabelResetConfirmation ? (
          <>
            <FooterText $row={imageLabelResetRow} data-testid='project-settings-image-label-reset-warning'>
              <span className='font-size_s font-weight_bold'>{t('project.settings.imageLabelReset.title')}</span>
              <span className='font-size_s'>{t('project.settings.imageLabelReset.details')}</span>
            </FooterText>
            <FooterCheckbox $row={imageLabelResetRow}>
              <Checkbox
                asAtom
                label={t('project.settings.imageLabelReset.confirm')}
                value={imageLabelResetConfirmed ? Checkbox.Selection.selected : Checkbox.Selection.unselected}
                onValueChange={onImageLabelResetConfirmationChange}
                data-testid='project-settings-image-label-reset-checkbox'
              />
            </FooterCheckbox>
          </>
        ) : null}
      </SettingsOutputFooter>
    </OutputFooterOverlay>
  );
}

const OutputFooterOverlay = styled.div`
  position: absolute;
  right: 0;
  bottom: 4rem;
  left: 0;
  z-index: 9;
`;

const SettingsOutputFooter = styled(OutputFooter)`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  justify-content: stretch;
  column-gap: 1rem;
  row-gap: 1rem;
  height: auto;
  min-height: 5.5rem;
`;

const FooterFlag = styled.div<{ $rowSpan: number }>`
  grid-column: 1;
  grid-row: 1 / span ${({ $rowSpan }) => $rowSpan};
  align-self: start;
`;

const FooterText = styled.div<{ $row: number }>`
  grid-column: 2;
  grid-row: ${({ $row }) => $row};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const FooterCheckbox = styled.div<{ $row: number }>`
  grid-column: 3;
  grid-row: ${({ $row }) => $row};
  align-self: center;
`;
